import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { Subscription, forkJoin, of } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { catchError, map } from 'rxjs/operators';

import { KontrollterminService, Kontrolltermin } from '../services/kontrolltermin.service';
import { MassnahmeService, Massnahme } from '../services/massnahme.service';
import { MitarbeiterService, TechnicianOption } from '../services/mitarbeiter.service';
import { StandortService, Standort } from '../services/standort.service';
import { KundeService } from '../services/kunde.service';
import { JournalService } from '../@core/journal.service';
import { TableExportService, ExportColumn } from '../services/table-export.service';
import { PaginationService } from '../core/services/pagination.service';
import { environment } from '../../environments/environment';
import { CreateKontrollterminDialogComponent } from './dialogs/create-kontrolltermin-dialog/create-kontrolltermin-dialog.component';
import { BulkEditKontrollterminDialogComponent, BulkEditResult } from './dialogs/bulk-edit-kontrolltermin-dialog/bulk-edit-kontrolltermin-dialog.component';

type Step =
  | 'geplant'
  | 'aktuell'
  | 'bearbeitung'
  | 'freigabe'
  | 'abgeschlossen'
  | 'others';

type TriStateFilter = 'all' | 'without' | 'with';

@Component({
  selector: 'app-kontrolltermine',
  templateUrl: './kontrolltermine.component.html',
  styleUrls: ['./kontrolltermine.component.scss']
})
export class KontrolltermineComponent implements OnInit, OnDestroy {
  isLoading = false;
  // Tab management - Freigabe is the default (index 3)
  activeTabIndex = 3;

  // Tab data
  kontrolltermine: Kontrolltermin[] = [];
  massnahmen: Massnahme[] = [];
  
  // Loading states for tabs
  kontrolltermineLoading = false;
  massnahmenLoading = false;

  // Search and filters
  searchTerm = '';
  selectedProjektaufgabeId = '';
  selectedStatusId = '';
  selectedKundeId = '';

  // New search and filter properties for autocomplete
  kundeSearchValue = '';
  verantwortlicherSearchValue = '';
  statusSearchValue = '';
  
  // Selected filter values
  selectedKunde: any = null;
  selectedVerantwortlicher: any = null;
  selectedStatus: any = null;
  
  // Autocomplete visibility flags
  showKundeAutocomplete = false;
  showStatusAutocomplete = false;
  showStandortAutocomplete = false;
  
  // Others dropdown
  showOthersDropdown = false;
  isOthersActive = false; // Track if we're viewing others data
  
  // Status tracking for edit navigation
  currentActiveStatus: string = 'freigabe'; // Track current status

  // Bulk selection (Geplant tab only) - kept by id so it survives paging
  selectedIds = new Set<string>();
  
  // Sorting state
  sortColumn: string | null = null;  // Current sort column
  sortDirection: 'asc' | 'desc' = 'desc';  // Current sort direction
  
  // Filtered autocomplete results
  filteredKunden: any[] = [];
  filteredStatus: any[] = [];
  filteredStandorte: any[] = [];

  // Pagination
  currentPage = 1;
  pageIndex = 0; // 0-based page index for mat-paginator
  pageSize: number;
  pageSizeOptions: number[];
  totalItems = 0;
  totalPages = 0;

  // Dropdown data
  projektaufgaben: any[] = [];
  status: any[] = [];
  kunden: any[] = [];
  techniker: TechnicianOption[] = [];
  standorte: Standort[] = [];
  
  // Month filter data
  months = [
    { value: '01', label: 'Januar' },
    { value: '02', label: 'Februar' },
    { value: '03', label: 'März' },
    { value: '04', label: 'April' },
    { value: '05', label: 'Mai' },
    { value: '06', label: 'Juni' },
    { value: '07', label: 'Juli' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'Oktober' },
    { value: '11', label: 'November' },
    { value: '12', label: 'Dezember' }
  ];
  
  // Selected filter values
  selectedMonth = '';
  selectedStandort: any = null;
  standortSearchValue = '';
  reportFilterState: TriStateFilter = 'all';
  sendFilterState: TriStateFilter = 'all';

  // Others dropdown options
  othersOptions = [
    { value: 'geloescht', label: 'Gelöscht', description: 'Gelöschte Kontrolltermine' },
    { value: 'abgebrochen', label: 'Abgebrochen', description: 'Abgebrochene Kontrolltermine' },
    { value: 'fehlerhaft', label: 'Fehlerhaft', description: 'Fehlerhafte Kontrolltermine' }
  ];

  // Table columns - update to show new fields
  displayedColumns = [
    'einsatznummer', 
    'standort', 
    'projektaufgabe', 
    'kunde', 
    'techniker', 
    'einsatztyp', 
    'datum_durchfuerung', 
    'status', 
    'global_befall', 
    'actions'
  ];

 // Tab order is tied to this array's index
readonly steps: Step[] = [
  'geplant',
  'aktuell',
  'bearbeitung',
  'freigabe',
  'abgeschlossen',
];

// Programmatic selection - Freigabe is the default (index 3)
selectedIndex = signal(3);

select(step: Step) {
  const idx = this.steps.indexOf(step);
  if (idx >= 0) {
    this.selectedIndex.set(idx);
    this.activeTabIndex = idx;
    this.isOthersActive = false; // Deactivate others when regular tab is selected
    this.currentActiveStatus = step; // Track current status
    this.clearSelection();
    // Persist selected tab so we can restore it when navigating back
    try { localStorage.setItem('kontrolltermine_active_tab', step); } catch(e) {}
    this.loadDataForStep(step);
  }
}

// For a11y (aria-selected)
isSelected(step: Step) {
  if (step === 'others') {
    // Check if others dropdown is open to show the indicator
    return this.showOthersDropdown;
  }
  // If others is active, no regular tab should be selected
  if (this.isOthersActive) {
    return false;
  }
  return this.steps[this.selectedIndex()] === step;
}

/**
 * Load data based on selected step
 */
loadDataForStep(step: Step) {
  this.currentActiveStatus = step; // Track current status
  
  // Reset sort when changing tabs (will use default sort for the view)
  this.sortColumn = null;
  this.sortDirection = 'desc';
  
  switch (step) {
    case 'geplant':
      this.loadKontrolltermineByStatus('geplant');
      break;
    case 'aktuell':
      this.loadKontrolltermineByStatus('aktuell');
      break;
    case 'bearbeitung':
      this.loadKontrolltermineByStatus('bearbeitung');
      break;
    case 'freigabe':
      this.loadKontrolltermineByStatus('freigabe');
      break;
    case 'abgeschlossen':
      this.loadKontrolltermineByStatus('abgeschlossen');
      break;
    case 'others':
      // This is now handled by the dropdown, so we don't load data here
      break;
  }
}

/**
 * Load kontrolltermine filtered by status
 */
loadKontrolltermineByStatus(status: string) {
  this.kontrolltermineLoading = true;
  
  // Map status to status_id for filtering
  let statusId: number | undefined;
  switch (status) {
    case 'geplant':
      statusId = 4; // geplant
      break;
    case 'aktuell':
      // Aktuell is a filter for geplant kontrolltermine within 30 days
      // We'll handle this with date filtering instead of status_id
      statusId = 4; // geplant
      break;
    case 'bearbeitung':
      statusId = 6; // in_bearbeitung
      break;
    case 'freigabe':
      statusId = 7; // freigabe
      break;
    case 'abgeschlossen':
      statusId = 8; // abgeschlossen
      break;
    case 'geloescht':
      statusId = 1; // geloescht
      break;
    case 'abgebrochen':
      statusId = 2; // abgebrochen
      break;
    case 'fehlerhaft':
      statusId = 3; // fehlerhaft
      break;
    default:
      statusId = undefined;
  }
  
  // For aktuell, we need to filter by date (next 30 days)
  let dateFrom: string | undefined;
  let dateTo: string | undefined;
  
  if (status === 'aktuell') {
    const today = new Date();
    
    // Calculate start date: 10 days before the 1st of current month
    const firstDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const startDate = new Date(firstDayOfCurrentMonth);
    startDate.setDate(startDate.getDate() - 10); // Go back 10 days
    
    // Calculate end date: last day of next month
    const lastDayOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0); // Day 0 = last day of previous month
    
    dateFrom = startDate.toISOString().split('T')[0]; // YYYY-MM-DD
    dateTo = lastDayOfNextMonth.toISOString().split('T')[0]; // YYYY-MM-DD
    
    console.log('Aktuell filter: dateFrom =', dateFrom, 'dateTo =', dateTo);
  }

  // Set default sort for specific views if no sort is active
  if (!this.sortColumn) {
    if (['bearbeitung', 'freigabe', 'abgeschlossen'].includes(status)) {
      this.sortColumn = 'datum_durchfuerung';
      this.sortDirection = 'desc';
    } else {
      this.sortColumn = 'einsatznummer';
      this.sortDirection = 'desc';
    }
  }

  this.listRequest?.unsubscribe();
  this.listRequest = this.kontrollterminService.getKontrolltermine(
    statusId,
    this.selectedMonth,
    this.selectedStandort?.id,
    this.selectedKunde?.id, // Apply kunde filter
    this.selectedVerantwortlicher?.id, // Apply techniker filter
    undefined, // einsatztyp
    dateFrom,
    dateTo,
    this.searchTerm, // Apply search term
    this.currentPage, // Apply pagination
    this.pageSize, // Apply page size
    undefined, // global_befall
    this.sortColumn,        // Pass sort column
    this.sortDirection,      // Pass sort direction
    status === 'abgeschlossen' ? this.mapReportFilterToApi(this.reportFilterState) : 'all',
    status === 'abgeschlossen' ? this.mapSendFilterToApi(this.sendFilterState) : 'all'
  ).subscribe({
    next: (response) => {
      console.log('Kontrolltermine loaded:', response);
      if (response.length > 0) {
        console.log('First kontrolltermin data:', response[0]);
        console.log('Kunde data:', response[0].kunde);
        console.log('Standort data:', response[0].standort);
        console.log('Techniker data:', response[0].techniker);
      }
      // Handle pagination - if API returns pagination metadata, use it
      // Otherwise, implement client-side pagination
      if (response && typeof response === 'object' && 'data' in response && 'total' in response) {
        // Server-side pagination response
        const paginatedResponse = response as { data: Kontrolltermin[], total: number };
        this.kontrolltermine = paginatedResponse.data;
        this.totalItems = paginatedResponse.total;
        this.totalPages = Math.ceil(paginatedResponse.total / this.pageSize);
      } else {
        // Client-side pagination: API returns all items, we need to slice them
        const allKontrolltermine = response as Kontrolltermin[];
        this.totalItems = allKontrolltermine.length;
        this.totalPages = Math.ceil(allKontrolltermine.length / this.pageSize);
        
        // Calculate pagination slice
        const startIndex = (this.currentPage - 1) * this.pageSize;
        const endIndex = startIndex + this.pageSize;
        this.kontrolltermine = allKontrolltermine.slice(startIndex, endIndex);
      }
      
      // Update pageIndex to sync with currentPage
      this.pageIndex = this.currentPage - 1;
      
      this.kontrolltermineLoading = false;
    },
    error: (error) => {
      console.error('Error loading kontrolltermine:', error);
      this.snackBar.open('Fehler beim Laden der Kontrolltermine', 'Schließen', { duration: 3000 });
      this.kontrolltermineLoading = false;
    }
  });
}

  /**
   * Handle column header click for sorting
   */
  sortBy(column: string) {
    // Toggle direction if clicking the same column
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      // New column, default to descending
      this.sortColumn = column;
      this.sortDirection = 'desc';
    }
    
    // Reset to first page when sorting changes
    this.currentPage = 1;
    this.pageIndex = 0;
    
    // Reload data with new sort parameters
    this.loadKontrolltermineByStatus(this.currentActiveStatus);
  }

  /**
   * Get sort icon for a column header
   */
  getSortIcon(column: string): string | null {
    if (this.sortColumn !== column) {
      return null; // No icon if not the active sort column
    }
    return this.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  /**
   * Check if a column is currently being sorted
   */
  isSortActive(column: string): boolean {
    return this.sortColumn === column;
  }

  // Swapped out on every reload, so a slow response can never overwrite a
  // newer one. Navigating back re-enters this component while the previous
  // list request may still be in flight.
  private listRequest?: Subscription;

  constructor(
    private kontrollterminService: KontrollterminService,
    private massnahmeService: MassnahmeService,
    private mitarbeiterService: MitarbeiterService,
    private standortService: StandortService,
    private kundeService: KundeService,
    private journalService: JournalService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private tableExportService: TableExportService,
    private paginationService: PaginationService
  ) {
    // Initialize pagination from service
    this.pageSize = this.paginationService.getPageSize();
    this.pageSizeOptions = this.paginationService.getPageSizeOptions();
  }

  ngOnInit() {
    // Load only dropdown data initially, not kontrolltermine
    this.loadDropdowns();
    // Restore previously selected tab, or default to 'freigabe'
    let savedTab = 'freigabe';
    try {
      const stored = localStorage.getItem('kontrolltermine_active_tab');
      if (stored) savedTab = stored;
    } catch(e) {}
    const othersValues = this.othersOptions.map(o => o.value);
    if (this.steps.includes(savedTab as Step)) {
      this.select(savedTab as Step);
    } else if (othersValues.includes(savedTab)) {
      this.selectOthersOption(savedTab);
    } else {
      this.select('freigabe');
    }
    // The edit view is a sibling route, so returning from it re-creates this
    // component and the tab restore above reloads the list. A NavigationEnd
    // listener on top of that only risks loading the same page twice.
  }

  ngOnDestroy() {
    this.listRequest?.unsubscribe();
  }

  /**
   * Load initial data
   */
  loadInitialData() {
    this.loadKontrolltermine();
    this.loadDropdowns();
  }

  /**
   * Load kontrolltermine with pagination and filters
   */
  loadKontrolltermine() {
    this.kontrolltermineLoading = true;
    this.kontrollterminService.getKontrolltermine(
      this.selectedStatusId ? parseInt(this.selectedStatusId) : undefined,
      this.selectedMonth,
      this.selectedStandort?.id
    ).subscribe({
      next: (response) => {
        this.kontrolltermine = response;
        this.totalItems = response.length;
        this.totalPages = Math.ceil(response.length / this.pageSize);
        
        
        this.kontrolltermineLoading = false;
      },
      error: (error) => {
        console.error('Error loading kontrolltermine:', error);
        this.snackBar.open('Fehler beim Laden der Kontrolltermine', 'Schließen', { duration: 3000 });
        this.kontrolltermineLoading = false;
      }
    });
  }

  /**
   * Load massnahmen
   */
  loadMassnahmen() {
    this.massnahmenLoading = true;
    this.massnahmeService.getMassnahmen().subscribe({
      next: (response) => {
        this.massnahmen = response.data;
        this.massnahmenLoading = false;
      },
      error: (error) => {
        console.error('Error loading massnahmen:', error);
        this.snackBar.open('Fehler beim Laden der Maßnahmen', 'Schließen', { duration: 3000 });
        this.massnahmenLoading = false;
      }
    });
  }

  /**
   * Load dropdown data
   */
  loadDropdowns() {
    // Load techniker data (all active, already sorted by nachname/vorname)
    this.mitarbeiterService.getTechnicians().subscribe({
      next: (response) => {
        this.techniker = response.data || [];
      },
      error: (error) => {
        console.error('Error loading techniker:', error);
      }
    });

    // Load all standorte (cached after the first list page visit)
    this.standortService.getAllStandorteForFilter().subscribe({
      next: (response) => {
        this.standorte = response.data || [];
        console.log('Loaded standorte for filter:', this.standorte.length);
      },
      error: (error) => {
        console.error('Error loading standorte:', error);
      }
    });

    // Load all kunden (cached after the first list page visit)
    this.kundeService.getAllKundenForFilter().subscribe({
      next: (response) => {
        this.kunden = response.data || [];
        console.log('Loaded kunden for filter:', this.kunden.length);
      },
      error: (error) => {
        console.error('Error loading kunden:', error);
      }
    });
  }

  /**
   * Handle tab change
   */
  onTabChange(event: MatTabChangeEvent) {
    this.activeTabIndex = event.index;
    
    if (this.activeTabIndex === 0) {
      this.loadKontrolltermine();
    } else if (this.activeTabIndex === 1) {
      this.loadMassnahmen();
    }
  }

  /**
   * Search kontrolltermine
   */
  onSearch() {
    this.currentPage = 1;
    this.clearSelection();
    // Load data for current selected step
    const currentStep = this.steps[this.selectedIndex()];
    this.loadDataForStep(currentStep);
  }

  /**
   * Clear search
   */
  clearSearch() {
    this.searchTerm = '';
    this.currentPage = 1;
    this.clearSelection();
    // Load data for current selected step
    const currentStep = this.steps[this.selectedIndex()];
    this.loadDataForStep(currentStep);
  }

  /**
   * Apply filters
   */
  applyFilters() {
    this.currentPage = 1;
    this.pageIndex = 0;
    this.clearSelection();
    // Load data for current selected step
    const currentStep = this.steps[this.selectedIndex()];
    this.loadDataForStep(currentStep);
  }

  /**
   * Clear all filters
   */
  clearFilters() {
    this.selectedProjektaufgabeId = '';
    this.selectedStatusId = '';
    this.selectedKundeId = '';
    this.selectedKunde = null;
    this.selectedVerantwortlicher = null;
    this.selectedStatus = null;
    this.selectedMonth = '';
    this.selectedStandort = null;
    this.kundeSearchValue = '';
    this.verantwortlicherSearchValue = '';
    this.statusSearchValue = '';
    this.standortSearchValue = '';
    this.reportFilterState = 'all';
    this.sendFilterState = 'all';
    this.currentPage = 1;
    this.clearSelection();
    // Load data for current selected step
    const currentStep = this.steps[this.selectedIndex()];
    this.loadDataForStep(currentStep);
  }

  /**
   * Handle techniker selection
   */
  onTechnikerChange(technikerId: string) {
    this.selectedVerantwortlicher = technikerId ? this.techniker.find(t => t.id.toString() === technikerId) : null;
    this.verantwortlicherSearchValue = this.selectedVerantwortlicher ? 
      this.selectedVerantwortlicher.display_name : '';
    this.applyFilters();
  }

  /**
   * Change page
   */
  onPageChange(event: any) {
    this.currentPage = event.pageIndex + 1;
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.paginationService.setPageSize(event.pageSize);
    // Load data for current selected step
    const currentStep = this.steps[this.selectedIndex()];
    this.loadDataForStep(currentStep);
  }


  /**
   * Bulk selection is only offered on the Geplant tab
   */
  isGeplantTab(): boolean {
    return this.currentActiveStatus === 'geplant';
  }

  clearSelection() {
    this.selectedIds.clear();
  }

  isRowSelected(kontrolltermin: Kontrolltermin): boolean {
    return this.selectedIds.has(kontrolltermin.id);
  }

  toggleRowSelection(kontrolltermin: Kontrolltermin) {
    if (this.selectedIds.has(kontrolltermin.id)) {
      this.selectedIds.delete(kontrolltermin.id);
    } else {
      this.selectedIds.add(kontrolltermin.id);
    }
  }

  areAllOnPageSelected(): boolean {
    return this.kontrolltermine.length > 0 &&
      this.kontrolltermine.every(kt => this.selectedIds.has(kt.id));
  }

  areSomeOnPageSelected(): boolean {
    return this.kontrolltermine.some(kt => this.selectedIds.has(kt.id)) &&
      !this.areAllOnPageSelected();
  }

  toggleSelectAllOnPage() {
    if (this.areAllOnPageSelected()) {
      this.kontrolltermine.forEach(kt => this.selectedIds.delete(kt.id));
    } else {
      this.kontrolltermine.forEach(kt => this.selectedIds.add(kt.id));
    }
  }

  /**
   * Open bulk edit dialog for the selected kontrolltermine
   */
  openBulkEditDialog() {
    if (this.selectedIds.size === 0) return;

    const dialogRef = this.dialog.open(BulkEditKontrollterminDialogComponent, {
      width: '520px',
      data: { count: this.selectedIds.size }
    });

    dialogRef.afterClosed().subscribe((result: BulkEditResult | undefined) => {
      if (!result) return;

      if (result.action === 'delete') {
        this.applyBulkUpdate({ status_id: 1 }, 'Kontrolltermine wurden gelöscht');
      } else {
        this.applyBulkUpdate(result.changes, 'Kontrolltermine wurden aktualisiert');
      }
    });
  }

  /**
   * Apply the same partial update to every selected kontrolltermin
   */
  private applyBulkUpdate(changes: Partial<Kontrolltermin>, successMessage: string) {
    const ids = Array.from(this.selectedIds);
    this.kontrolltermineLoading = true;

    const requests = ids.map(id =>
      this.kontrollterminService.updateKontrolltermin(id, changes).pipe(
        map(() => true),
        catchError(error => {
          console.error(`Error updating kontrolltermin ${id}:`, error);
          return of(false);
        })
      )
    );

    forkJoin(requests).subscribe(results => {
      const succeeded = results.filter(ok => ok).length;
      const failed = results.length - succeeded;

      if (failed === 0) {
        this.snackBar.open(`${successMessage} (${succeeded})`, 'Schließen', { duration: 3000 });
      } else {
        this.snackBar.open(
          `${succeeded} von ${results.length} Kontrollterminen aktualisiert, ${failed} fehlgeschlagen`,
          'Schließen',
          { duration: 5000 }
        );
      }

      this.clearSelection();
      this.loadKontrolltermineByStatus(this.currentActiveStatus);
    });
  }

  /**
   * Edit kontrolltermin
   */
  editKontrolltermin(kontrolltermin: Kontrolltermin, event?: Event) {
    if (event) {
      event.stopPropagation(); // Prevent row click
    }
    
    // Get the actual status of the kontrolltermin
    const actualStatus = this.getStatusKeyFromId(kontrolltermin.status_id);
    
    // For aktuell tab, use the actual status (geplant) instead of the tab status
    const sourceStatus = (this.currentActiveStatus === 'aktuell' && actualStatus === 'geplant') 
      ? 'geplant' 
      : this.currentActiveStatus;
    
    // Console logging for status tracking
    console.log('🔍 EDIT TRIGGERED:');
    console.log('📊 Current Active Status:', this.currentActiveStatus);
    console.log('📋 Kontrolltermin Status ID:', kontrolltermin.status_id);
    console.log('📋 Kontrolltermin Status Name:', this.getStatusLabel(kontrolltermin.status_id));
    console.log('📋 Actual Status:', actualStatus);
    console.log('📋 Source Status (used):', sourceStatus);
    console.log('🎯 Status Match:', this.currentActiveStatus === actualStatus);
    console.log('🌐 Navigating to:', `/kontrolltermine/${kontrolltermin.id}?mode=edit&sourceStatus=${sourceStatus}`);
    
    // Pass the correct source status as query parameter
    this.router.navigate(['/kontrolltermine', kontrolltermin.id], { 
      queryParams: { 
        mode: 'edit',
        sourceStatus: sourceStatus // Use actual status for aktuell tab
      } 
    });
  }

  /**
   * Delete kontrolltermin
   */
  deleteKontrolltermin(kontrolltermin: Kontrolltermin) {
    // TODO: Implement delete confirmation
    this.snackBar.open(`Löschen: ${kontrolltermin.id}`, 'Schließen', { duration: 2000 });
  }

  /**
   * View kontrolltermin details
   */
  viewKontrolltermin(kontrolltermin: Kontrolltermin) {
    console.log('👁️ VIEW TRIGGERED:');
    console.log('📊 Current Active Status:', this.currentActiveStatus);
    console.log('📋 Kontrolltermin Status ID:', kontrolltermin.status_id);
    
    this.router.navigate(['/kontrolltermine', kontrolltermin.id], { 
      queryParams: { 
        mode: 'view',
        sourceStatus: this.currentActiveStatus // Pass current status
      } 
    });
  }

  /**
   * Create new massnahme
   */
  createMassnahme() {
    // TODO: Implement create dialog
    this.snackBar.open('Erstellen-Funktion wird implementiert', 'Schließen', { duration: 2000 });
  }

  /**
   * Edit massnahme
   */
  editMassnahme(massnahme: Massnahme) {
    // TODO: Implement edit dialog
    this.snackBar.open(`Bearbeiten: ${massnahme.beschreibung}`, 'Schließen', { duration: 2000 });
  }

  /**
   * Delete massnahme
   */
  deleteMassnahme(massnahme: Massnahme) {
    // TODO: Implement delete confirmation
    this.snackBar.open(`Löschen: ${massnahme.beschreibung}`, 'Schließen', { duration: 2000 });
  }

  /**
   * Get CSS class for status badge
   */
  getStatusClass(statusCode?: string): string {
    if (!statusCode) return 'status-badge inactive';
    
    switch (statusCode.toLowerCase()) {
      case 'active':
      case 'aktiv':
        return 'status-badge active';
      case 'completed':
      case 'abgeschlossen':
        return 'status-badge completed';
      case 'pending':
      case 'ausstehend':
        return 'status-badge pending';
      default:
        return 'status-badge inactive';
    }
  }

  // New autocomplete methods for search and filter functionality

  /**
   * Kunde search and filter methods
   */
  onKundeSearch(event: any) {
    const value = event.target.value;
    this.kundeSearchValue = value;
    
    if (value.length > 0) {
      this.filteredKunden = this.kunden.filter(kunde => 
        kunde.suchbegriff?.toLowerCase().includes(value.toLowerCase()) ||
        kunde.name?.toLowerCase().includes(value.toLowerCase()) ||
        kunde.strasse?.toLowerCase().includes(value.toLowerCase())
      );
    } else {
      // Show all clients when search is empty
      this.filteredKunden = this.kunden;
    }
  }

  onKundeBlur() {
    setTimeout(() => {
      this.showKundeAutocomplete = false;
    }, 200);
  }

  onKundeFocus() {
    // Show all clients when focusing on the input
    this.filteredKunden = this.kunden;
    this.showKundeAutocomplete = true;
  }

  selectKunde(kunde: any) {
    this.selectedKunde = kunde;
    this.kundeSearchValue = kunde.suchbegriff || kunde.name;
    this.showKundeAutocomplete = false;
    this.applyFilters();
  }

  clearKundeFilter() {
    this.selectedKunde = null;
    this.kundeSearchValue = '';
    this.applyFilters();
  }

  getKundeDisplayValue(): string {
    if (this.selectedKunde) {
      return this.selectedKunde.suchbegriff || this.selectedKunde.name;
    }
    return this.kundeSearchValue;
  }

  /**
   * Clear techniker filter
   */
  clearVerantwortlicherFilter() {
    this.selectedVerantwortlicher = null;
    this.verantwortlicherSearchValue = '';
    this.applyFilters();
  }

  /**
   * Status search and filter methods
   */
  onStatusSearch(event: any) {
    const value = event.target.value;
    this.statusSearchValue = value;
    
    if (value.length > 0) {
      this.filteredStatus = this.status.filter(status => 
        status.beschreibung?.toLowerCase().includes(value.toLowerCase()) ||
        status.code?.toLowerCase().includes(value.toLowerCase())
      );
    } else {
      this.filteredStatus = [];
    }
  }

  onStatusBlur() {
    setTimeout(() => {
      this.showStatusAutocomplete = false;
    }, 200);
  }

  selectStatus(status: any) {
    this.selectedStatus = status;
    this.statusSearchValue = status.beschreibung;
    this.showStatusAutocomplete = false;
    this.applyFilters();
  }

  clearStatusFilter() {
    this.selectedStatus = null;
    this.statusSearchValue = '';
    this.applyFilters();
  }

  getStatusDisplayValue(): string {
    if (this.selectedStatus) {
      return this.selectedStatus.beschreibung;
    }
    return this.statusSearchValue;
  }

  /**
   * Month filter methods
   */
  onMonthChange(monthValue: string) {
    this.selectedMonth = monthValue;
    this.applyFilters();
  }

  /**
   * Standort search and filter methods
   */
  onStandortSearch(event: any) {
    const value = event.target.value;
    this.standortSearchValue = value;
    
    if (value.length > 0) {
      this.filteredStandorte = this.standorte.filter(standort => 
        standort.name?.toLowerCase().includes(value.toLowerCase()) ||
        standort.nummer?.toString().includes(value)
      );
    } else {
      // Show all standorte when search is empty
      this.filteredStandorte = this.standorte;
    }
  }

  onStandortBlur() {
    setTimeout(() => {
      this.showStandortAutocomplete = false;
    }, 200);
  }

  onStandortFocus() {
    // Show all standorte when focusing on the input
    this.filteredStandorte = this.standorte;
    this.showStandortAutocomplete = true;
  }

  selectStandort(standort: any) {
    this.selectedStandort = standort;
    this.standortSearchValue = standort.name;
    this.showStandortAutocomplete = false;
    this.applyFilters();
  }

  clearStandortFilter() {
    this.selectedStandort = null;
    this.standortSearchValue = '';
    this.applyFilters();
  }

  getStandortDisplayValue(): string {
    if (this.selectedStandort) {
      return this.selectedStandort.name;
    }
    return this.standortSearchValue;
  }

  /**
   * Get CSS classes for status badge based on status_id
   */
  getStatusClasses(statusId: number): string {
    switch (statusId) {
      case 1: // geloescht
        return 'bg-gray-50 text-gray-700 ring-gray-600/20 dark:bg-gray-900/30 dark:text-gray-400 dark:ring-gray-500/50';
      case 2: // abgebrochen
        return 'bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-900/30 dark:text-red-400 dark:ring-red-500/50';
      case 3: // fehlerhaft
        return 'bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-900/30 dark:text-red-400 dark:ring-red-500/50';
      case 4: // geplant
        return 'bg-blue-50 text-blue-700 ring-blue-600/20 dark:bg-blue-900/30 dark:text-blue-400 dark:ring-blue-500/50';
      case 5: // aktuell
        return 'bg-yellow-50 text-yellow-700 ring-yellow-600/20 dark:bg-yellow-900/30 dark:text-yellow-400 dark:ring-yellow-500/50';
      case 6: // in_bearbeitung
        return 'bg-orange-50 text-orange-700 ring-orange-600/20 dark:bg-orange-900/30 dark:text-orange-400 dark:ring-orange-500/50';
      case 7: // freigabe
        return 'bg-purple-50 text-purple-700 ring-purple-600/20 dark:bg-purple-900/30 dark:text-purple-400 dark:ring-purple-500/50';
      case 8: // abgeschlossen
        return 'bg-green-50 text-green-700 ring-green-600/20 dark:bg-green-900/30 dark:text-green-400 dark:ring-green-500/50';
      default:
        return 'bg-slate-50 text-slate-700 ring-slate-600/20 dark:bg-slate-900/30 dark:text-slate-400 dark:ring-slate-500/50';
    }
  }

  /**
   * Get status label by status_id
   */
  getStatusLabel(statusId: number): string {
    const status = environment.kontrolltermin_status.find(s => s.id === statusId);
    return status?.label || 'Unbekannt';
  }

  /**
   * Get befall label for tooltip
   */
  getBefallLabel(befall: number | null | undefined): string {
    if (befall === 1) return 'Befall vorhanden';
    if (befall === 0) return 'Kein Befall';
    return 'Unbekannt';
  }

  /**
   * Show others dropdown
   */
  openOthersDropdown() {
    this.showOthersDropdown = true;
  }

  /**
   * Hide others dropdown
   */
  hideOthersDropdown() {
    this.showOthersDropdown = false;
  }

  /**
   * Handle others option selection
   */
  selectOthersOption(value: string) {
    this.hideOthersDropdown();
    this.isOthersActive = true; // Keep others button active
    this.currentActiveStatus = value; // Track current status
    this.clearSelection();
    try { localStorage.setItem('kontrolltermine_active_tab', value); } catch(e) {}
    
    // Map the selected value to the appropriate status and load data
    switch (value) {
      case 'geloescht':
        this.loadKontrolltermineByStatus('geloescht');
        break;
      case 'abgebrochen':
        this.loadKontrolltermineByStatus('abgebrochen');
        break;
      case 'fehlerhaft':
        this.loadKontrolltermineByStatus('fehlerhaft');
        break;
    }
  }

  /**
   * Get status key from status ID
   */
  getStatusKeyFromId(statusId: number): string {
    const statusMap: {[key: number]: string} = {
      1: 'geloescht',
      2: 'abgebrochen', 
      3: 'fehlerhaft',
      4: 'geplant',
      5: 'aktuell',
      6: 'bearbeitung',
      7: 'freigabe',
      8: 'abgeschlossen'
    };
    return statusMap[statusId] || 'unknown';
  }

  /**
   * Get German month name from date string
   */
  getGermanMonthName(dateString: string | undefined): string {
    if (!dateString) return '-';
    try {
      // Parse date string directly to avoid timezone issues
      // Date format is typically "YYYY-MM-DD" or "YYYY-MM-DD HH:MM:SS"
      const dateParts = dateString.split(/[-T\s]/);
      if (dateParts.length < 2) return '-';
      
      const month = parseInt(dateParts[1], 10);
      if (isNaN(month) || month < 1 || month > 12) return '-';
      
      const monthNames = [
        'Jänner', 'Februar', 'März', 'April', 'Mai', 'Juni',
        'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
      ];
      return monthNames[month - 1] || '-'; // month is 1-12, array index is 0-11
    } catch (error) {
      return '-';
    }
  }

  getYearFromDate(dateString: string | undefined): string {
    if (!dateString) return '';
    try {
      const dateParts = dateString.split(/[-T\s]/);
      if (dateParts.length < 1) return '';
      const year = parseInt(dateParts[0], 10);
      return isNaN(year) ? '' : year.toString();
    } catch (error) {
      return '';
    }
  }

  /**
   * Format date for export
   */
  formatDate(dateString: string | undefined): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    } catch (error) {
      return '-';
    }
  }

  /**
   * Format standort info (name and address) for export
   */
  formatStandortInfo(kontrolltermin: any): string {
    if (!kontrolltermin.standort) return '-';
    
    const standort = kontrolltermin.standort;
    const parts: string[] = [];
    
    // Add standort name
    if (standort.name) {
      parts.push(standort.name);
    }
    
    // Add address
    const addressParts: string[] = [];
    if (standort.strasse) {
      addressParts.push(standort.strasse);
    }
    if (standort.plz && standort.ort) {
      addressParts.push(`${standort.plz} ${standort.ort}`);
    } else if (standort.plz) {
      addressParts.push(standort.plz);
    } else if (standort.ort) {
      addressParts.push(standort.ort);
    }
    
    if (addressParts.length > 0) {
      parts.push(addressParts.join(', '));
    }
    
    return parts.length > 0 ? parts.join(', ') : '-';
  }

  /**
   * Check if exact date should be shown below the month
   * Only for: In-bearbeitung (bearbeitung), Freigabe, and Abgeschlossen
   */
  shouldShowExactDate(): boolean {
    return ['bearbeitung', 'freigabe', 'abgeschlossen'].includes(this.currentActiveStatus);
  }

  isAbgeschlossenTab(): boolean {
    return this.currentActiveStatus === 'abgeschlossen';
  }

  cycleReportFilter(): void {
    this.reportFilterState = this.nextTriState(this.reportFilterState);
    this.currentPage = 1;
    this.pageIndex = 0;
    this.loadKontrolltermineByStatus('abgeschlossen');
  }

  cycleSendFilter(): void {
    this.sendFilterState = this.nextTriState(this.sendFilterState);
    this.currentPage = 1;
    this.pageIndex = 0;
    this.loadKontrolltermineByStatus('abgeschlossen');
  }

  private nextTriState(current: TriStateFilter): TriStateFilter {
    const order: TriStateFilter[] = ['all', 'without', 'with'];
    return order[(order.indexOf(current) + 1) % order.length];
  }

  private mapReportFilterToApi(state: TriStateFilter): 'all' | 'with_report' | 'without_report' {
    switch (state) {
      case 'with': return 'with_report';
      case 'without': return 'without_report';
      default: return 'all';
    }
  }

  private mapSendFilterToApi(state: TriStateFilter): 'all' | 'sent' | 'not_sent' {
    switch (state) {
      case 'with': return 'sent';
      case 'without': return 'not_sent';
      default: return 'all';
    }
  }

  getReportFilterTooltip(): string {
    switch (this.reportFilterState) {
      case 'with': return 'Nur mit Bericht';
      case 'without': return 'Nur ohne Bericht';
      default: return 'Alle Berichte';
    }
  }

  getSendFilterTooltip(): string {
    switch (this.sendFilterState) {
      case 'with': return 'Nur versendet';
      case 'without': return 'Nur nicht versendet';
      default: return 'Alle Versandstatus';
    }
  }

  getReportFilterThumbIcon(): string {
    switch (this.reportFilterState) {
      case 'with': return 'description';
      case 'without': return 'block';
      default: return 'filter_none';
    }
  }

  getSendFilterThumbIcon(): string {
    switch (this.sendFilterState) {
      case 'with': return 'mail';
      case 'without': return 'mark_email_unread';
      default: return 'filter_none';
    }
  }

  hasReportGenerated(kontrolltermin: Kontrolltermin): boolean {
    return kontrolltermin.has_report_generated
      ?? !!(kontrolltermin.freigabe?.bericht_generiert_datum && kontrolltermin.freigabe?.bericht_link);
  }

  hasReportSent(kontrolltermin: Kontrolltermin): boolean {
    return kontrolltermin.has_report_sent
      ?? !!(kontrolltermin.freigabe?.versand_datum && kontrolltermin.freigabe?.bericht_versand_durch);
  }

  getReportGeneratedTooltip(kontrolltermin: Kontrolltermin): string {
    if (!this.hasReportGenerated(kontrolltermin)) {
      return 'Kein Bericht erstellt';
    }
    const date = kontrolltermin.freigabe?.bericht_generiert_datum;
    return date ? `Bericht erstellt am ${this.formatDate(date)}` : 'Bericht erstellt';
  }

  getReportSentTooltip(kontrolltermin: Kontrolltermin): string {
    if (!this.hasReportSent(kontrolltermin)) {
      return 'Bericht nicht versendet';
    }
    const date = kontrolltermin.freigabe?.versand_datum;
    return date ? `Versendet am ${this.formatDate(date)}` : 'Bericht versendet';
  }

  /**
   * Create new kontrolltermin via dialog
   */
  createKontrolltermin(): void {
    const dialogRef = this.dialog.open(CreateKontrollterminDialogComponent, {
      width: '600px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.event === 'create') {
        const kontrollterminData = result.data;
        
        this.kontrollterminService.createKontrolltermin(kontrollterminData).subscribe({
          next: (createdKontrolltermin) => {
            // Log journal entry with entity IDs
            const entityIds = this.journalService.extractEntityIds(createdKontrolltermin);
            this.journalService.log(
              'Kontrolltermin erstellt',
              '',
              `Kontrolltermin: ${createdKontrolltermin?.einsatznummer || 'Unbekannt'}`,
              entityIds
            );
            
            this.snackBar.open('Kontrolltermin erfolgreich erstellt', 'Schließen', { duration: 3000 });
            // Reload current tab data
            const currentStep = this.steps[this.selectedIndex()];
            this.loadDataForStep(currentStep);
          },
          error: (error) => {
            console.error('Error creating kontrolltermin:', error);
            this.snackBar.open('Fehler beim Erstellen des Kontrolltermins', 'Schließen', { duration: 3000 });
          }
        });
      } else if (result && result.event === 'error') {
        this.snackBar.open('Fehler beim Erstellen der Projektaufgabe', 'Schließen', { duration: 3000 });
      }
    });
  }

  /**
   * Export table data to CSV
   */
  exportToCSV(): void {
    try {
      const exportColumns: ExportColumn<Kontrolltermin>[] = [
        { key: 'datum_durchfuerung', label: 'Prüfdatum', transform: (val) => this.formatDate(val) },
        { key: 'einsatznummer', label: 'Einsatznummer' },
        { key: 'kunde.suchbegriff', label: 'Kunde' },
        { key: 'standort.name', label: 'Standort' },
        { key: 'standort_info', label: 'Standort Info', transform: (val, row) => this.formatStandortInfo(row) },
        { key: 'techniker.name', label: 'Techniker', transform: (val, row) => {
          const tech = row.techniker;
          if (!tech) return '-';
          // Try multiple possible property combinations
          if ((tech as any).full_name) return (tech as any).full_name;
          if ((tech as any).display_name) return (tech as any).display_name;
          if ((tech as any).vorname && (tech as any).nachname) {
            return `${(tech as any).vorname} ${(tech as any).nachname}`.trim();
          }
          if ((tech as any).name) return (tech as any).name;
          return '-';
        }},
        { key: 'projektaufgabe.beschreibung', label: 'Projektaufgabe' },
        { key: 'einsatztyp', label: 'Einsatztyp' },
        { key: 'global_befall', label: 'Befall', transform: (val) => this.getBefallLabel(val) },
        { key: 'status_id', label: 'Status', transform: (val) => this.getStatusLabel(val) },
        { key: 'anmerkungen', label: 'Anmerkungen' },
        { key: 'soll_zeit', label: 'Soll Zeit' },
        { key: 'ist_zeit', label: 'Ist Zeit' },
        { key: 'created_at', label: 'Erstellt am', transform: (val) => this.formatDate(val) },
        { key: 'updated_at', label: 'Aktualisiert am', transform: (val) => this.formatDate(val) }
      ];

      this.tableExportService.exportTable({
        data: this.kontrolltermine,
        columns: exportColumns,
        filename: 'kontrolltermine',
        format: 'csv',
        includeHeaders: true
      });

      this.snackBar.open('Kontrolltermine erfolgreich als CSV exportiert', 'Schließen', { duration: 3000 });
    } catch (error) {
      console.error('Error exporting kontrolltermine:', error);
      this.snackBar.open('Fehler beim Exportieren der Kontrolltermine', 'Schließen', { duration: 3000 });
    }
  }

  /**
   * Export table data to Excel
   */
  async exportToExcel(): Promise<void> {
    try {
      const exportColumns: ExportColumn<Kontrolltermin>[] = [
        { key: 'datum_durchfuerung', label: 'Prüfdatum', transform: (val) => this.formatDate(val) },
        { key: 'einsatznummer', label: 'Einsatznummer' },
        { key: 'kunde.suchbegriff', label: 'Kunde' },
        { key: 'standort.name', label: 'Standort' },
        { key: 'standort_info', label: 'Standort Info', transform: (val, row) => this.formatStandortInfo(row) },
        { key: 'techniker.name', label: 'Techniker', transform: (val, row) => {
          const tech = row.techniker;
          if (!tech) return '-';
          // Try multiple possible property combinations
          if ((tech as any).full_name) return (tech as any).full_name;
          if ((tech as any).display_name) return (tech as any).display_name;
          if ((tech as any).vorname && (tech as any).nachname) {
            return `${(tech as any).vorname} ${(tech as any).nachname}`.trim();
          }
          if ((tech as any).name) return (tech as any).name;
          return '-';
        }},
        { key: 'projektaufgabe.beschreibung', label: 'Projektaufgabe' },
        { key: 'einsatztyp', label: 'Einsatztyp' },
        { key: 'global_befall', label: 'Befall', transform: (val) => this.getBefallLabel(val) },
        { key: 'status_id', label: 'Status', transform: (val) => this.getStatusLabel(val) },
        { key: 'anmerkungen', label: 'Anmerkungen' },
        { key: 'soll_zeit', label: 'Soll Zeit' },
        { key: 'ist_zeit', label: 'Ist Zeit' },
        { key: 'created_at', label: 'Erstellt am', transform: (val) => this.formatDate(val) },
        { key: 'updated_at', label: 'Aktualisiert am', transform: (val) => this.formatDate(val) }
      ];

      await this.tableExportService.exportTable({
        data: this.kontrolltermine,
        columns: exportColumns,
        filename: 'kontrolltermine',
        format: 'xlsx',
        includeHeaders: true
      });

      this.snackBar.open('Kontrolltermine erfolgreich als Excel exportiert', 'Schließen', { duration: 3000 });
    } catch (error) {
      console.error('Error exporting kontrolltermine:', error);
      this.snackBar.open('Fehler beim Exportieren der Kontrolltermine', 'Schließen', { duration: 3000 });
    }
  }

}
